import {
  Box,
  Center,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  useDisclosure,
  useToast,
  Button,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';

import {
  useDeactivateBankAccountMutation,
  GetBankAccountsWithInstitutionAndOwnerByImplementationIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  toastId,
  successToastConfig,
  errorToastId,
  errorToastAdminConfig,
} from '~/packages/common-frontend/constants';

export const DeactivateBankAccountConfirmation = ({
  bankAccountId,
  institutionName,
  accountName,
}: {
  bankAccountId: string;
  institutionName: string;
  accountName: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const handleClose = () => {
    onClose();
    setIsSubmitting(false);
  };

  const [deactivateBankAccount] = useDeactivateBankAccountMutation({
    refetchQueries: [
      GetBankAccountsWithInstitutionAndOwnerByImplementationIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
    ],
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastAdminConfig.title = `Error deactivating bank account. Please try again.`;
        toast(errorToastAdminConfig);
        handleClose();
      }
    },
  });

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const { data: deactivateBankAccountData } = await deactivateBankAccount({
        variables: {
          bankAccountId,
        },
      });

      if (
        deactivateBankAccountData &&
        deactivateBankAccountData.deactivateBankAccount &&
        !toast.isActive(toastId)
      ) {
        const response =
          deactivateBankAccountData.deactivateBankAccount.response;
        successToastConfig.title = response;
        toast(successToastConfig);
      } else {
        handleClose();
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastAdminConfig.title = `Error deactivating bank account. Please try again.`;
        toast(errorToastAdminConfig);
        setIsSubmitting(false);
      }
      throw err;
    }
  };

  return (
    <Box>
      <IconButtonV2
        aria-label="Deactivate"
        icon={<Icon icon="trash" />}
        placement="bottom"
        variant="v2-ghost"
        colorScheme="red"
        onClick={() => {
          Sentry.addBreadcrumb({
            category: 'ui.click',
            message: `Clicked deactivate bank account`,
            level: Sentry.Severity.Info,
          });
          onOpen();
        }}
      />
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Deactivate bank account</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text marginBottom="20px">
              Please confirm that you would like to deactivate this bank
              account:
            </Text>
            <Text fontWeight="bold">
              {institutionName}: {accountName}
            </Text>
            <Center mt="30px">
              <HStack spacing="20px">
                <Button
                  type="submit"
                  variant="primary"
                  colorScheme="red"
                  width="fit-content"
                  isLoading={isSubmitting}
                  onClick={handleSubmit}
                >
                  Deactivate
                </Button>
                <Button
                  variant="secondary"
                  colorScheme="gray"
                  width="fit-content"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </HStack>
            </Center>
            <Box height="25px" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
